<script lang="ts" setup>
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { useDisplay, useTheme } from 'vuetify'
import type { ChatContact as TypeChatContact } from '@/types/chat'
import { themes } from '@/plugins/vuetify/theme'
import ChatLeftSidebarContent from '@/views/apps/chat/ChatLeftSidebarContent.vue'
import ChatLog from '@/views/apps/chat/ChatLog.vue'
import { useChatStore } from '@/views/apps/chat/useChatStore'
import { useUserStore } from '@core/stores/user'
import { App } from '@/types/generated'
import UserData = App.Http.Controllers.Auth.UserData

definePage({
  meta: {
    layoutWrapperClasses: 'layout-content-height-fixed',
    action: 'read',
    subject: 'chat',
  },
})

const { t } = useI18n()

const vuetifyDisplays = useDisplay()
const store = useChatStore()
const { isLeftSidebarOpen } = useResponsiveLeftSidebar(vuetifyDisplays.smAndDown)
const userStore = useUserStore()
const userData = useCookie<UserData>('userData')
const userRoles: string [] = userData.value?.roles ?? []
const chatLogPS = ref()

const scrollToBottomInChatLog = () => {
  const scrollEl = chatLogPS.value?.$el || chatLogPS.value

  userStore.chatLogPS = scrollEl
  scrollEl.scrollTop = scrollEl.scrollHeight
}

// Search query
const q = ref('')

watch(
  q,
  val => store.fetchChatsAndContacts(val),
  { immediate: true },
)

// Open Sidebar in smAndDown when "start conversation" is clicked
const startConversation = () => {
  if (vuetifyDisplays.mdAndUp.value)
    return
  isLeftSidebarOpen.value = true
}

// Chat message
const msg = ref('')

const sendMessage = async () => {
  if (!msg.value)
    return

  await store.sendMsg(msg.value)

  // Reset message input
  msg.value = ''

  // Scroll to bottom
  nextTick(() => {
    scrollToBottomInChatLog()
  })
}

const newLine = async (e: any) => {
  const caret = e.target.selectionStart

  e.target.setRangeText('\n', caret, caret, 'end')
  msg.value = e.target.value
}

const openChatOfContact = async (userId: TypeChatContact['id']) => {
  await store.getChat(userId)

  // Reset message input
  msg.value = ''

  // Set unseenMsgs to 0
  const contact = store.chatsContacts.find(c => c.id === userId)
  if (contact)
    contact.chat.unseenMsgs = 0

  // if smAndDown =>  Close Chat & Contacts left sidebar
  if (vuetifyDisplays.smAndDown.value)
    isLeftSidebarOpen.value = false

  // Scroll to bottom
  nextTick(() => {
    scrollToBottomInChatLog()
  })
}

// User profile sidebar
const isUserProfileSidebarOpen = ref(false)

// Active chat user profile sidebar
const isActiveChatUserProfileSidebarOpen = ref(false)

const { name } = useTheme()

const chatContentContainerBg = computed(() => themes[name.value].colors?.background || 'transparent')

if (store.activeChat !== null)
  setTimeout(() => scrollToBottomInChatLog(), 1)

if (store.userIdAfterRedirect !== '') {
  openChatOfContact(store.userIdAfterRedirect)
  store.userIdAfterRedirect = ''
}
</script>

<template>
  <VLayout class="chat-app-layout">
    <!-- 👉 Left sidebar   -->
    <VNavigationDrawer
      v-model="isLeftSidebarOpen"
      absolute
      touchless
      location="start"
      width="370"
      :temporary="$vuetify.display.smAndDown"
      class="chat-list-sidebar"
      :permanent="$vuetify.display.mdAndUp"
    >
      <ChatLeftSidebarContent
        v-model:isDrawerOpen="isLeftSidebarOpen"
        v-model:search="q"
        @open-chat-of-contact="openChatOfContact"
        @show-user-profile="isUserProfileSidebarOpen = true"
        @close="isLeftSidebarOpen = false"
      />
    </VNavigationDrawer>

    <!-- 👉 Chat content -->
    <VMain class="chat-content-container">
      <!-- 👉 Right content: Active Chat -->
      <div
        v-if="store.activeChat"
        class="d-flex flex-column h-100"
      >
        <!-- 👉 Active chat header -->
        <div class="active-chat-header d-flex align-center text-medium-emphasis bg-surface">
          <!-- Sidebar toggler -->
          <IconBtn
            class="d-md-none me-3"
            @click="isLeftSidebarOpen = true"
          >
            <VIcon icon="tabler-menu-2" />
          </IconBtn>

          <!-- avatar -->
          <div
            class="d-flex align-center cursor-pointer"
            @click="isActiveChatUserProfileSidebarOpen = true"
          >
            <VAvatar
              size="38"
              :variant="!store.activeChat?.contact ? 'tonal' : undefined"
            >
              <VImg
                v-if="store.activeChat?.contact?.avatar"
                :src="`/api/account/avatar-${store.activeChat?.contact?.id}`"
                :alt="store.activeChat?.contact?.full_name"
              />
              <span v-else>{{ avatarText(store.activeChat?.contact?.full_name) }}</span>
            </VAvatar>

            <div class="flex-grow-1 ms-4 overflow-hidden">
              <p class="text-truncate mb-0 text-body-2">
                {{ store.activeChat?.contact?.full_name }}
                <span v-if="store.activeChat?.contact?.last_game_number !== null && (userIsAdmin(userRoles) || userIsManager(userRoles) || userIsSupport(userRoles))">
                  | {{ t('chat-page.last_game') }}: #{{ store.activeChat?.contact?.last_game_number }}
                </span>
              </p>
            </div>
          </div>

          <VSpacer />

          <!-- Header right content -->
        </div>

        <VDivider />

        <!-- Chat log -->
        <PerfectScrollbar
          ref="chatLogPS"
          tag="ul"
          :options="{ wheelPropagation: false }"
          class="flex-grow-1"
        >
          <ChatLog />
        </PerfectScrollbar>

        <!-- Message form -->
        <VForm
          class="chat-log-message-form mb-5 mx-5"
          @submit.prevent="sendMessage"
        >
          <AppTextarea
            :key="store.activeChat?.contact.id"
            v-model="msg"
            variant="solo"
            auto-grow
            rows="1"
            :placeholder="t('chat-page.type')"
            density="default"
            autofocus
            @keyup.enter.prevent.exact="sendMessage"
            @keydown.ctrl.enter.prevent.exact="newLine"
          >
            <template #append-inner>
              <VBtn @click="sendMessage">
                <template #append>
                  <VIcon
                    icon="tabler-send"
                    color="#fff"
                  />
                </template>
                {{ t('chat-page.send') }}
              </VBtn>
            </template>
          </AppTextarea>
        </VForm>
      </div>

      <!-- 👉 Start conversation -->
      <div
        v-else
        class="d-flex h-100 align-center justify-center flex-column"
      >
        <VAvatar
          size="98"
          variant="tonal"
          color="primary"
          class="mb-4"
        >
          <VIcon
            size="50"
            class="rounded-0"
            icon="tabler-message-2"
          />
        </VAvatar>

        <VBtn
          v-if="$vuetify.display.smAndDown"
          rounded="xl"
          @click="startConversation"
        >
          {{ t('chat-page.start_conversation') }}
        </VBtn>

        <p
          v-else
          style="max-inline-size: 40ch; text-wrap: balance;"
          class="text-center text-disabled"
        >
          {{ t('chat-page.start_connecting') }}
        </p>
      </div>
    </VMain>
  </VLayout>
</template>

<style lang="scss">
@use "@styles/variables/vuetify.scss";
@use "@core-scss/base/mixins.scss";
@use "@layouts/styles/mixins" as layoutsMixins;

// Variables
$chat-app-header-height: 76px;

// Placeholders
%chat-header {
  display: flex;
  align-items: center;
  min-block-size: $chat-app-header-height;
  padding-inline: 1.5rem;
}

.chat-start-conversation-btn {
  cursor: default;
}

.chat-app-layout {
  border-radius: vuetify.$card-border-radius;

  @include mixins.elevation(vuetify.$card-elevation);

  $sel-chat-app-layout: &;

  @at-root {
    .skin--bordered {
      @include mixins.bordered-skin($sel-chat-app-layout);
    }
  }

  .active-chat-user-profile-sidebar,
  .user-profile-sidebar {
    .v-navigation-drawer__content {
      display: flex;
      flex-direction: column;
    }
  }

  .chat-list-header,
  .active-chat-header {
    @extend %chat-header;
  }

  .chat-list-sidebar {
    .v-navigation-drawer__content {
      display: flex;
      flex-direction: column;
    }
  }
}

.chat-content-container {
  /* stylelint-disable-next-line value-keyword-case */
  background-color: v-bind(chatContentContainerBg);

  // Adjust the padding so text field height stays 48px
  .chat-message-input {
    .v-field__input {
      font-size: 0.9375rem !important;
      line-height: 1.375rem !important;
      padding-block: 0.6rem 0.5rem;
    }

    .v-field__append-inner {
      align-items: center;
      padding-block-start: 0;
    }

    .v-field--appended {
      padding-inline-end: 8px;
    }
  }
}

.chat-user-profile-badge {
  .v-badge__badge {
    /* stylelint-disable liberty/use-logical-spec */
    min-width: 12px !important;
    height: 0.75rem;
    /* stylelint-enable liberty/use-logical-spec */
  }
}
</style>
